import { useCallback, useEffect, useState } from "react";
import { useDueDiligenceFeedback } from "../useDueDiligenceFeedback";
import { DueDiligenceFeedbackQuestionsConfig, DueDiligenceFeedbackStatus } from "../useApi/Schemas";
import { isErr } from "../../Utils/Result";
import { eventBus } from "../../EventBus";
import { Notification } from "../../../Components/Notifications";

/**
 * @param creditsGranted - What the feedback earned; absent when giving it failed.
 * @returns The thanks, with the credits when there were any.
 */
const thanksMessage = (creditsGranted: number | undefined): string =>
  creditsGranted
    ? `¡Gracias por tu feedback! Te hemos abonado ${creditsGranted.toLocaleString("es-ES")} ${creditsGranted === 1 ? "crédito" : "créditos"}.`
    : "¡Gracias por tu feedback!";

// Shared behavior behind the feedback survey, independent of which page hosts
// it (CaseDetails, FinalReport...): fetch status+questions once the case is
// completed, submit, and dismiss. Kept out of any one page so both can reuse
// it without duplicating the fetch/submit state machine.
export const useDueDiligenceFeedbackGate = (caseId: string | undefined, isCompleted: boolean) => {
  const { getFeedbackStatus, getFeedbackQuestions, submitFeedback, dismissFeedbackPrompt } =
    useDueDiligenceFeedback();

  const [feedbackStatus, setFeedbackStatus] = useState<DueDiligenceFeedbackStatus | null>(null);
  const [feedbackConfig, setFeedbackConfig] = useState<DueDiligenceFeedbackQuestionsConfig | null>(null);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const [initialNpsScore, setInitialNpsScore] = useState<number | null>(null);

  useEffect(() => {
    if (!caseId || !isCompleted) return;

    let cancelled = false;
    (async () => {
      const [statusResult, questionsResult] = await Promise.all([
        getFeedbackStatus(caseId),
        getFeedbackQuestions(),
      ]);
      if (cancelled) return;
      if (!isErr(statusResult)) setFeedbackStatus(statusResult.value);
      if (!isErr(questionsResult)) setFeedbackConfig(questionsResult.value.config);
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, isCompleted]);

  const handleSubmitFeedback = useCallback(
    async (data: { npsScore: number; answers: Record<string, string> }) => {
      if (!caseId) return;
      setIsSubmittingFeedback(true);
      const result = await submitFeedback(caseId, data);
      setIsSubmittingFeedback(false);

      if (isErr(result)) {
        eventBus.emit(
          "NOTIFICATION",
          Notification.buildNotification("error", "No se ha podido enviar tu feedback")
        );
        return;
      }

      setFeedbackStatus((prev) => (prev ? { ...prev, alreadySubmitted: true, shouldAutoPrompt: false } : prev));

      setIsFeedbackModalOpen(false);
      eventBus.emit("NOTIFICATION", Notification.buildNotification("success", thanksMessage(result.value.creditsGranted)));
    },
    [caseId, submitFeedback]
  );

  const handleDismissFeedbackPrompt = useCallback(() => {
    setFeedbackStatus((prev) => (prev ? { ...prev, shouldAutoPrompt: false } : prev));
    dismissFeedbackPrompt();
  }, [dismissFeedbackPrompt]);

  return {
    feedbackStatus,
    feedbackConfig,
    isFeedbackModalOpen,
    isSubmittingFeedback,
    initialNpsScore,
    // Plain open, no score chosen yet — the CaseDetails manual button.
    openFeedbackModal: useCallback(() => {
      setInitialNpsScore(null);
      setIsFeedbackModalOpen(true);
    }, []),
    // Open with a score already picked — the footer's per-circle click.
    openFeedbackModalWithScore: useCallback((score: number) => {
      setInitialNpsScore(score);
      setIsFeedbackModalOpen(true);
    }, []),
    closeFeedbackModal: useCallback(() => {
      setIsFeedbackModalOpen(false);
    }, []),
    handleSubmitFeedback,
    handleDismissFeedbackPrompt,
  };
};
